/**
 * alan.ts
 *
 * NE İŞE YARAR : Bir alanın yanıtta **bulunup bulunmadığını** söyler — `null` ile
 *                *"alan yok"*u ayırır
 * KAYNAK SPEC  : 15-arayuz-standardi.md §7.4 (alan bazlı gizleme) · §7.1
 *                03-alan-politikasi.md §2 (`GİZLİ` maskesi) · §6 (serileştirme profilleri)
 *                ADR-009 §3, §5 · 04-api-sozlesmesi.md §2.8
 * BAĞLI OLDUĞU : — (saf mantık)
 * KULLANAN     : src/yetki/alan.tsx · src/yetki/sutun.ts · bütün detay ve liste ekranları
 * DİKKAT       : 1) BU DOSYANIN VAR OLMA SEBEBİ TEK BİR CÜMLEDİR (`15` §7.4):
 *                   *"Arayüz bunu `null` sanmamalıdır."* Sunucu, çağıranın profilinde
 *                   olmayan alanı yanıta **hiç koymaz** (`03` §2 — `GİZLİ`); `null`
 *                   koymaz, boş metin koymaz, alan **yoktur**. İki durum tek yerde
 *                   ayrıştırılmazsa her ekran kendi yorumunu yapar ve biri bir gün
 *                   *"Cinsiyet: —"* yazar. O satır, alanın **var olduğunu** söyler ve
 *                   sunucunun özenle kapattığı bilgiyi geri açar.
 *                2) ÖLÇÜT `in`DİR, `!== undefined` DEĞİL. Sunucu bir alanı açıkça
 *                   `null` gönderebilir (değer yok ama alan var); `undefined` ise
 *                   JSON'da hiç bulunmaz. `!== undefined` ikisini de doğru ayırır ama
 *                   `in`, niyeti okunur kılar: sorulan şey **anahtarın varlığıdır**.
 *                3) İKİ SORU AYRIDIR VE KARIŞTIRILAMAZ:
 *                     `alanVar`  → çizilsin mi        (yetki sorusu)
 *                     `bosMu`    → *"—"* mi yazılsın  (veri sorusu)
 *                   Tek bir *"göster/gösterme"* fonksiyonu yazılsaydı, veri boş
 *                   olduğunda alan gizlenir ve kullanıcı yetkisi olmadığını sanardı.
 *                4) İÇ İÇE ALANLAR (`person.first_name`) BU DOSYANIN KONUSU DEĞİL.
 *                   `03` §6.2 profillerde noktalı adlar gösteriyor ama arka uç bugün
 *                   düz kayıt döndürüyor; iç içe yanıt geldiğinde burası genişler.
 *                   Şimdiden yazmak, doğrulanmamış bir biçimi desteklemek olurdu.
 * SON GÜNCELLEME: 2026-08-31 04:40:00
 */

/** Yanıt kaydı — anahtarları çağıranın profiline göre değişir (`04` §2.8). */
export type Kayit = Readonly<Record<string, unknown>>;

/**
 * Alan yanıtta **var mı** — değeri ne olursa olsun (başlıktaki notlar 1–2).
 *
 * `false` demek *"bu kullanıcı bu alanı göremez"* demektir; satır **hiç çizilmez**.
 */
export function alanVar(kayit: Kayit | null | undefined, ad: string): boolean {
  return kayit !== null && kayit !== undefined && ad in kayit;
}

/** Alanın değeri — yoksa `undefined`. Karar için `alanVar` kullanılır, bu değil. */
export function alanDegeri(kayit: Kayit | null | undefined, ad: string): unknown {
  return kayit === null || kayit === undefined ? undefined : kayit[ad];
}

/**
 * Değer boş mu — *"—"* yazılıp yazılmayacağı (başlıktaki not 3).
 *
 * **Alanın yokluğu burada sorulmaz.** Yok olan bir alan için bu fonksiyon hiç
 * çağrılmaz; çağrılsaydı `true` döner ve *"—"* yazılırdı — tam da `15` §7.4'ün
 * yasakladığı şey.
 */
export function bosMu(deger: unknown): boolean {
  return deger === null || deger === undefined || (typeof deger === 'string' && deger.trim() === '');
}

/** Boş değerin ekrandaki karşılığı (`15` §7.4). */
export const BOS_GOSTERIM = '—';

/**
 * Bir alanı metne çevirir — **yalnız alan varken** çağrılır.
 *
 * `bicim` verilirse boş olmayan değere uygulanır; boşsa `—` döner ve biçimlendirici
 * hiç çalışmaz. Çalışsaydı tarih biçimlendiricisi `null`ü *"01.01.1970"* yapardı.
 */
export function alanMetni(deger: unknown, bicim?: (deger: unknown) => string): string {
  if (bosMu(deger)) {
    return BOS_GOSTERIM;
  }
  return bicim === undefined ? String(deger) : bicim(deger);
}
